import {
	setExternalUserId,
	removeExternalUserId,
	getPlayerId,
	optInUser,
} from 'webtonative/OneSignal';
import { isNativeApp } from 'webtonative';

let isConfigured = false;

type BridgeCallResult<T> =
	| { succeeded: true; value: Awaited<T> }
	| { succeeded: false; value: null };

async function callNativeBridge<T>(callback: () => T | Promise<T>): Promise<BridgeCallResult<T>> {
	try {
		return { succeeded: true, value: await callback() };
	} catch {
		return { succeeded: false, value: null };
	}
}

// Consume the notification payload buffered by the inline <script> in index.html.
// That script captures cold-start clicks before the bundle even loads.
export function consumePendingNotification() {
	const data = window.__pendingNotificationData;
	window.__pendingNotificationData = null;
	return data;
}

/**
 * Re-bind the current user ID to the native OneSignal subscription.
 * Safe to call repeatedly — it's a lightweight bridge call.
 * This ensures that even if the native SDK received a new push token
 * (e.g. after iOS token rotation), it stays mapped to the correct user.
 */
export function rebindExternalUserId(userId: string) {
	if (!isNativeApp || !userId) return;

	void callNativeBridge(() => setExternalUserId(userId));
}

/**
 * Clear the external user ID from the native OneSignal subscription.
 * Should be called on logout to prevent pushes from reaching the wrong user.
 */
export function clearExternalUserId() {
	if (!isNativeApp) return;

	void callNativeBridge(() => removeExternalUserId());
}

export async function initOneSignal({
	userId,
}: {
	userId?: string;
	logClient?: (data: any) => void;
}) {
	if (!isNativeApp) return;

	if (!isConfigured) {
		const result = await callNativeBridge(() => optInUser());
		if (result.succeeded) {
			isConfigured = true;
		}
	}

	if (userId) {
		await callNativeBridge(() => setExternalUserId(userId));
	} else {
		clearExternalUserId();
	}

	void callNativeBridge(() => getPlayerId());

	// Re-register the handler now that React is ready — dispatch a CustomEvent
	// so the useOneSignalInit listener picks it up immediately.
	window.onWebToNativeNotificationClicked = (data: any) => {
		window.__pendingNotificationData = data;
		window.dispatchEvent(new CustomEvent('PUSH_NOTIFICATION_CLICK', { detail: data }));
	};
}
